import React, { useCallback, useEffect, useRef, useState, forwardRef, useImperativeHandle, useMemo } from 'react';
import { View, Platform, PanResponder, TouchableOpacity, Modal, Alert, Image } from 'react-native';
import { Text, Card, YStack, XStack, SizableText, Button, H2, Popover } from 'tamagui';
import { debounce } from 'lodash';
import Animated, { 
  useSharedValue, 
  useAnimatedProps, 
  useAnimatedStyle,
  useAnimatedReaction, 
  withTiming, 
  runOnJS 
} from 'react-native-reanimated';
import Svg, { Path, Defs, Marker } from 'react-native-svg';
import { MaterialIcons, Feather } from '@expo/vector-icons';
import * as Animatable from 'react-native-animatable';
import { themeSelector } from '@reduxLocal/selectors/themeSelector';
import styles, { getModernCardStyle, getModernTextColor } from '@stylesheet';
import { HierarchyPosition } from './BaseHierarchy';
import ActionDialog from '../../navigation/screens/MidLevel/DataScreen/ActionHandler/FlowDesigner/Components/ActionDialog';
import Measurable, { MeasurableHandle } from '@navigation/screens/MidLevel/UsersScreen/utils/MeasurableView';
import actionList from '@navigation/screens/MidLevel/DataScreen/ActionHandler/Components/utils/actionList';
import ErrorBoundary from '@utils/ErrorBoundary';
import { ScrollView } from '@constants/Themed';
import { getNodeType, getNodeColorByCategory, getNodeIcon, isWorkflowNodeType, NODE_CATEGORIES } from '@navigation/screens/MidLevel/DataScreen/ActionHandler/Components/utils/workflowNodeTypes';

export interface DraggableActionCardHandle {
  setPosition: (position: HierarchyPosition) => void;
  getPosition: () => HierarchyPosition;
  actionId: string;
  setArrowTranslate: (path: string, color: string) => void;
  disableComponent: () => void;
  enableComponent: () => void;
}

interface DraggableActionCardProps {
  action: any;
  initialPosition: HierarchyPosition;
  onPositionChange: (position: HierarchyPosition) => void;
  onSnap?: (targetId: string, sourceId: string) => void;
  dragEnabled?: boolean;
  portrait?: boolean;
  showArrows?: boolean;
  isPrimarySchema?: boolean;
  zoomVars?: any;
  accelerateValue?: number;
  transform?: { translateX: number; translateY: number };
  getAllActionPositions?: () => Record<string, HierarchyPosition>;
  index?: number;
  onActionClick?: (action: any) => void; // Accept full action object
  dataId?: string; // The data schema ID to save actions to
  // Connection management props
  onConnectionStart?: (actionId: string) => void;
  onConnectionEnd?: (actionId: string) => void;
  onConnectionCancel?: () => void;
  isConnecting?: boolean;
  connectionStart?: string | null;
  // Workflow node props
  isWorkflowNode?: boolean;
  nodeType?: string; // Specific node type (e.g., 'webhook', 'if', 'email')
  nodeCategory?: 'trigger' | 'action' | 'logic' | 'data' | 'error';
  onWorkflowNodeClick?: (nodeId: string) => void;
  // Agentic flow props
  isAgenticFlow?: boolean;
  agenticFlowConfig?: any;
}

const DraggableActionCardInner = forwardRef<DraggableActionCardHandle, DraggableActionCardProps>(
  (props, ref) => {
    const {
      action,
      initialPosition,
      onPositionChange,
      onSnap,
      dragEnabled = true,
      portrait = true,
      showArrows = false,
      isPrimarySchema = false,
      zoomVars = {},
      accelerateValue = 1,
      transform = { translateX: 0, translateY: 0 },
      getAllActionPositions,
      index = 0,
      onActionClick,
      dataId,
      // Connection management props
      onConnectionStart,
      onConnectionEnd,
      onConnectionCancel,
      isConnecting = false,
      connectionStart,
      // Workflow node props
      isWorkflowNode: propIsWorkflowNode,
      nodeType: propNodeType,
      nodeCategory: propNodeCategory,
      onWorkflowNodeClick,
      // Agentic flow props
      isAgenticFlow: propIsAgenticFlow,
      agenticFlowConfig: propAgenticFlowConfig
    } = props;

    const theme = themeSelector();
    
    // Workflow node detection
    const isWorkflowNode = propIsWorkflowNode || action.isWorkflowNode || action.nodeType !== undefined;
    const nodeCategory = propNodeCategory || action.nodeType || (isWorkflowNode ? getNodeType(action.type)?.category : undefined);
    const nodeType = propNodeType || action.type;
    
    // Agentic flow detection
    const isAgenticFlow = propIsAgenticFlow || action.agenticFlow?.enabled || action.config?.agenticFlow?.enabled;
    const agenticFlowConfig = propAgenticFlowConfig || action.agenticFlow || action.config?.agenticFlow;
    const nodeTypeDef = isWorkflowNode && nodeType ? getNodeType(nodeType) : null;
    const nodeColor = nodeTypeDef?.color || (nodeCategory ? getNodeColorByCategory(nodeCategory) : undefined);
    const nodeIcon = nodeTypeDef?.icon || (isWorkflowNode && nodeType ? getNodeIcon(nodeType) : undefined);
    
    // Render counter for debugging (only in dev mode)
    const renderCountRef = useRef(0);
    useEffect(() => {
      renderCountRef.current += 1;
      if (__DEV__ && renderCountRef.current > 2) { // Only log after initial renders
        console.log(`🔄 DraggableActionCard re-rendered: ${action._id} (${action.title || action.name}) - Count: ${renderCountRef.current} - IsWorkflowNode: ${isWorkflowNode}`);
      }
    });

    // State management similar to DraggableUserCard
    const [isDragEnabled, setIsDragEnabled] = useState(false);
    const [hideArrows, setHideArrows] = useState(false);
    const [showActionDialog, setShowActionDialog] = useState(false);
    const [currentActionType, setCurrentActionType] = useState<'Create' | 'Read' | 'Update' | 'Delete'>('Create');
    const [modalVisible, setModalVisible] = useState(false);
    const [showSchemaPopover, setShowSchemaPopover] = useState(false);
    const measureRef = useRef<MeasurableHandle>(null);
    const formRef = useRef(null);
    const isArrowRef = useRef(false);
    const startPositionRef = useRef({ x: 0, y: 0 });
    const componentMountedRef = useRef(true);
    const isDragMovingRef = useRef(false);
    const clickStartTimeRef = useRef(0);
    const hasDraggedRef = useRef(false);
    const [isPortrait, setIsPortrait] = useState(Platform.select({ web: portrait, native: true }));
    
    // Card dimensions based on orientation (matching DraggableUserCard)
    const cardWidth = portrait ? 320 : 200;
    const cardHeight = portrait ? 400 : 120;

    // Shared values for position management with persistence - use ref to prevent re-initialization
    const translationX = useSharedValue(0);
    const translationY = useSharedValue(0);
    const hasBeenInitialized = useRef(false);
    const lastKnownPosition = useRef<HierarchyPosition>(initialPosition);
    const sharedValuesInitialized = useRef(false);
    const initialPositionRef = useRef(initialPosition);

    // Prevent lastKnownPosition from being reset on re-renders
    if (!lastKnownPosition.current || (lastKnownPosition.current.x === 0 && lastKnownPosition.current.y === 0)) {
      lastKnownPosition.current = initialPosition;
    }

    // Arrow functionality shared values (from DraggableUserCard)
    const absoluteX = useSharedValue(0);
    const absoluteY = useSharedValue(0);
    const arrowPath = useSharedValue('M0,0 L0,0');
    const strokeColor = useSharedValue('#3b82f6');
    const angle = useSharedValue(270);
    const snapThreshold = 150;
    const ctx = useSharedValue({ 
      startX: 0, 
      startY: 0, 
      startAbsoluteX: 0, 
      startAbsoluteY: 0, 
      isSnapped: false, 
      snappedIndex: -1 
    });

    console.log('🎯 DraggableActionCard render:', {
      actionId: action._id,
      actionTitle: action.title,
      actionType: action.type,
      position: { x: translationX.value, y: translationY.value },
      dragEnabled,
      isDragEnabled,
      isPrimarySchema,
      cardDimensions: { width: cardWidth, height: cardHeight },
      hasAction: !!action,
      actionKeys: action ? Object.keys(action) : [],
      fullAction: action
    });

    // Imperative handle (matching DraggableUserCard structure)
    useImperativeHandle(ref, () => ({
      setPosition: (position: HierarchyPosition) => {
        console.log('🎯 Imperative setPosition called for action:', action._id, position);
        translationX.value = position.x;
        translationY.value = position.y;
        lastKnownPosition.current = position;
        hasBeenInitialized.current = true;
        onPositionChange(position);
      },
      getPosition: () => ({
        x: translationX.value,
        y: translationY.value
      }),
      actionId: action._id,
      setArrowTranslate: (path: string, color: string) => {
        arrowPath.value = path;
        strokeColor.value = color;
      },
      disableComponent: () => {
        setHideArrows(true);
      },
      enableComponent: () => {
        setHideArrows(false);
      },
    }), [action._id, translationX, translationY, onPositionChange]);

    // Position measurement callback for connection points
    const measureView = useCallback((e) => {
      const { x, y, width, height } = e;
      if (x !== undefined) {
        // Report top connection dot position for incoming connections
        const topDotX = x + translationX.value + (width / 1.7);
        const topDotY = y + translationY.value - 8; // Top connection dot is 18px above component, center of 16px dot = -8px from edge

        // This would be used by parent for connection management
        console.log('🎯 Action position measured:', { topDotX, topDotY, width, height, actionId: action._id });
      }
    }, [measureRef, isPortrait, action._id, translationX, translationY]);

    // Get the component if it exists from actionList
    const ComponentToRender = actionList.find(listAction => listAction.key === action.key)?.component

    // Position initialization - only run once per component mount
    useEffect(() => {
      if (!hasBeenInitialized.current && !sharedValuesInitialized.current) {
        // Use last known position if available, otherwise use initial position
        const positionToUse = (lastKnownPosition.current.x !== 0 || lastKnownPosition.current.y !== 0)
          ? lastKnownPosition.current
          : initialPositionRef.current;

        console.log('🎯 Action card initializing position for:', action._id, positionToUse);

        // Initialize shared values only once
        translationX.value = positionToUse.x;
        translationY.value = positionToUse.y;

        lastKnownPosition.current = positionToUse;
        hasBeenInitialized.current = true;
        sharedValuesInitialized.current = true;

        // Only call onPositionChange if this is a meaningful position change
        if (positionToUse.x !== 0 || positionToUse.y !== 0) {
          onPositionChange(positionToUse);
        }
      }
    }, [action._id]); // Only depend on action._id, not initialPosition

    // Preserve position during re-renders - prevent position resets
    useEffect(() => {
      if (hasBeenInitialized.current && sharedValuesInitialized.current) {
        // Preserve the current position from shared values to lastKnownPosition
        const currentPosition = {
          x: translationX.value,
          y: translationY.value
        };

        // Only update if the position has actually changed and is meaningful
        if (currentPosition.x !== lastKnownPosition.current.x ||
          currentPosition.y !== lastKnownPosition.current.y) {
          console.log('🎯 Preserving position during re-render:', action._id, currentPosition);
          lastKnownPosition.current = currentPosition;
        }
      }
    }, [translationX.value, translationY.value, action._id]);

    // Force positioning for primary schema to ensure it's centered - only when first becoming primary
    useEffect(() => {
      if (isPrimarySchema && hasBeenInitialized.current && initialPositionRef.current.x !== 0 && initialPositionRef.current.y !== 0) {
        const currentX = translationX.value;
        const currentY = translationY.value;
        const distance = Math.sqrt(Math.pow(currentX - initialPositionRef.current.x, 2) + Math.pow(currentY - initialPositionRef.current.y, 2));

        // Only animate if there's a significant position difference
        if (distance > 50) {
          console.log('🏛️ Force positioning primary schema:', action._id, initialPositionRef.current);
          translationX.value = withTiming(initialPositionRef.current.x, { duration: 500 });
          translationY.value = withTiming(initialPositionRef.current.y, { duration: 500 });
          onPositionChange(initialPositionRef.current);
        }
      }
    }, [isPrimarySchema]); // Only depend on isPrimarySchema, not position values

    // Debounced updates for better tracking performance
    const debouncedConnectionUpdate = useCallback(
      debounce((newPos: HierarchyPosition) => {
        if (!componentMountedRef.current) return;
        onPositionChange(newPos);
      }, 16), // Reduced from 50ms to 16ms for ~60fps update rate
      [onPositionChange]
    );

    // Immediate position update for real-time tracking
    const immediatePositionUpdate = useCallback((newPos: HierarchyPosition) => {
      if (!componentMountedRef.current) return;
      // Update position immediately for smooth visual tracking
      onPositionChange(newPos);
    }, [onPositionChange]);

    // Memoized click handlers
    const handlePrimarySchemaClick = useCallback(() => {
      console.log('🎯 Primary schema card clicked');
      // If onActionClick callback is provided, use that instead of showing popover
      if (onActionClick) {
        console.log('🎯 Calling onActionClick callback for primary schema');
        onActionClick(action);
      } else {
        console.log('🎯 Showing schema popover (no onActionClick provided)');
        setShowSchemaPopover(true);
      }
    }, [onActionClick, action]);

    const handleActionCardClick = useCallback((actionType: 'Create' | 'Read' | 'Update' | 'Delete') => {
      console.log('🎯 Normalized action type:', actionType);

      // Set the current action type for the dialog
      setCurrentActionType(actionType);

      // If onActionClick callback is provided, use that instead of internal modal
      // Pass full action object if available
      if (onActionClick) {
        console.log('🎯 Calling onActionClick callback with action object');
        onActionClick(action); // Pass full action object
      } else {
        // No callback provided, show internal dialog
        console.log('🎯 Setting showActionDialog to true');
        setShowActionDialog(true);
      }
    }, [onActionClick, action]);

    const normalizeActionType = useCallback((actionType: string): 'Create' | 'Read' | 'Update' | 'Delete' => {
      const normalizedType = actionType.toLowerCase();
      switch (normalizedType) {
        case 'create':
          return 'Create';
        case 'read':
          return 'Read';
        case 'update':
          return 'Update';
        case 'delete':
          return 'Delete';
        default:
          return 'Create'; // Default fallback
      }
    }, []);

    // Convert gesture values to view coordinates
    const convertGestureToViewCoordinates = useCallback(({
      absoluteX,
      absoluteY,
      translationX,
      translationY,
      parentOffset,
      zoomLevel,
    }) => {
      const adjustedX = (absoluteX - parentOffset.x) / zoomLevel;
      const adjustedY = (absoluteY - parentOffset.y) / zoomLevel;

      const translatedViewX = adjustedX + translationX / zoomLevel;
      const translatedViewY = adjustedY + translationY / zoomLevel;

      return { viewX: translatedViewX, viewY: translatedViewY };
    }, []);

    // Arrow PanResponder (from DraggableUserCard) - memoized to prevent recreation
    const arrowPanResponder = useMemo(() => PanResponder.create({
      onStartShouldSetPanResponder: () => showArrows && !isDragMovingRef.current && !isPrimarySchema,
      onMoveShouldSetPanResponder: () => showArrows && !isDragMovingRef.current && !isPrimarySchema,
      onPanResponderGrant: (event, gestureState) => {
        const { locationX: viewX, locationY: viewY } = event.nativeEvent;
        const currentZoomLevel = zoomVars?.zoomLevel || 1;
        
        ctx.value.startX = 0;
        ctx.value.startY = 0;
        ctx.value.startAbsoluteX = absoluteX.value;
        ctx.value.startAbsoluteY = absoluteY.value;
        ctx.value.isSnapped = false;
        ctx.value.snappedIndex = -1;
        
        console.log('🎯 Action arrow start:', { startX: ctx.value.startX, startY: ctx.value.startY, viewX, viewY });
        isArrowRef.current = true;
      },
      onPanResponderMove: (event, gestureState) => {
        const { dx: tX, dy: tY, moveX: ax, moveY: ay } = gestureState;
        const currentZoomLevel = zoomVars?.zoomLevel || 1;

        absoluteX.value = ax;
        absoluteY.value = ay;

        // Get all action positions for snap detection
        const allActionPositions = getAllActionPositions ? getAllActionPositions() : {};
        const currentActionPosition = allActionPositions[action._id] || { x: translationX.value, y: translationY.value };

        if (!allActionPositions || Object.keys(allActionPositions).length === 0) {
          const interpolatedX = tX / currentZoomLevel;
          const interpolatedY = tY / currentZoomLevel;
          const dragX = ctx.value.startX + interpolatedX;
          const dragY = ctx.value.startY + interpolatedY;

          const controlPoint1X = dragX * 0.5;
          const controlPoint1Y = dragY * 0.5;
          const pathValue = `M0,0 C${controlPoint1X},${controlPoint1Y} ${dragX * 0.8},${dragY * 0.8} ${dragX},${dragY}`;
          arrowPath.value = pathValue;
          strokeColor.value = '#3b82f6';
          return;
        }

        const interpolatedX = tX / currentZoomLevel;
        const interpolatedY = tY / currentZoomLevel;
        const dragX = ctx.value.startX + interpolatedX;
        const dragY = ctx.value.startY + interpolatedY;

        const currentCardCenterX = currentActionPosition.x + (cardWidth / 2);
        const currentCardCenterY = currentActionPosition.y + (cardHeight / 2);
        
        const worldDragX = currentCardCenterX + dragX;
        const worldDragY = currentCardCenterY + dragY;

        let snappedToAction = false;
        Object.entries(allActionPositions).forEach(([actionId, position], index) => {
          if (actionId === action._id) return;
          if (snappedToAction) return;
          
          const targetCenterX = position.x + (cardWidth / 2);
          const targetCenterY = position.y + (cardHeight / 2);
          
          const distance = Math.sqrt(
            Math.pow(targetCenterX - worldDragX, 2) + Math.pow(targetCenterY - worldDragY, 2)
          );

          if (distance < snapThreshold) {
            console.log(`✅ Action snapping to ${actionId}! Distance: ${distance.toFixed(1)}`);
            
            const relativeTargetX = targetCenterX - currentCardCenterX;
            const relativeTargetY = targetCenterY - currentCardCenterY;

            const controlPoint1X = relativeTargetX * 0.3;
            const controlPoint1Y = relativeTargetY * 0.3;
            const controlPoint2X = relativeTargetX * 0.7;
            const controlPoint2Y = relativeTargetY * 0.7;

            const arrowPathValue = `M0,0 C${controlPoint1X},${controlPoint1Y} ${controlPoint2X},${controlPoint2Y} ${relativeTargetX},${relativeTargetY}`;
            arrowPath.value = arrowPathValue;
            strokeColor.value = '#22c55e';
            angle.value = withTiming(90, { duration: 150 });
            
            ctx.value.snappedIndex = index;
            ctx.value.isSnapped = true;
            snappedToAction = true;
          }
        });

        if (!snappedToAction) {
          const controlPoint1X = dragX * 0.5;
          const controlPoint1Y = dragY * 0.5;
          const pathValue = `M0,0 C${controlPoint1X},${controlPoint1Y} ${dragX * 0.8},${dragY * 0.8} ${dragX},${dragY}`;
          arrowPath.value = pathValue;
          strokeColor.value = '#3b82f6';
          angle.value = Math.atan2(tY, tX) * (180 / Math.PI);
          ctx.value.isSnapped = false;
        }
      },
      onPanResponderRelease: () => {
        const allActionPositions = getAllActionPositions ? getAllActionPositions() : {};
        const snappedActionId = ctx.value.snappedIndex !== -1 ? 
          Object.keys(allActionPositions)[ctx.value.snappedIndex] : null;
        
        if (snappedActionId && onSnap) {
          runOnJS(onSnap)(snappedActionId, action._id);
        }
        
        strokeColor.value = '#3b82f6';
        angle.value = 270;
        arrowPath.value = 'M0,0 L0,0';
        ctx.value.isSnapped = false;
        ctx.value.snappedIndex = -1;
        isArrowRef.current = false;
      },
    }), [
      showArrows,
      isDragMovingRef,
      isPrimarySchema,
      zoomVars,
      ctx,
      absoluteX,
      absoluteY,
      arrowPath,
      strokeColor,
      angle,
      snapThreshold,
      cardWidth,
      cardHeight,
      translationX,
      translationY,
      action._id,
      getAllActionPositions,
      onSnap
    ]);

    // Drag PanResponder (from DraggableUserCard) - memoized to prevent recreation
    const dragPanResponder = useMemo(() => PanResponder.create({
      onStartShouldSetPanResponder: () => {
        // Capture touch immediately so we can handle both clicks and drags
        const shouldCapture = dragEnabled && !isPrimarySchema;
        console.log('🎯 onStartShouldSetPanResponder:', { shouldCapture, dragEnabled, isPrimarySchema });
        return shouldCapture;
      },
      onMoveShouldSetPanResponder: () => {
        // Always allow movement once we've captured the touch
        return dragEnabled && !isPrimarySchema;
      },
      onPanResponderGrant: (event, gestureState) => {
        console.log('🎯 PAN RESPONDER GRANTED - Touch started on action card:', action._id, action.title);
        // Capture the current position at the start of the gesture
        const currentPosition = { x: translationX.value, y: translationY.value };
        startPositionRef.current = currentPosition;
        
        // Track click start time and reset drag flag
        clickStartTimeRef.current = Date.now();
        hasDraggedRef.current = false;
        
        // Don't set isDragEnabled immediately - wait to see if it's a drag or click
        isDragMovingRef.current = false;
        
        console.log('🎯 Action touch start for:', action._id, 'captured position:', currentPosition, 'gesture:', {
          x0: gestureState.x0,
          y0: gestureState.y0
        });
      },
      onPanResponderMove: (event, gestureState) => {
        if (!dragEnabled || isPrimarySchema) return;
        
        const { dx, dy } = gestureState;
        
        // Track if user has dragged significantly (threshold for click vs drag)
        const dragThreshold = 5;
        if (Math.abs(dx) > dragThreshold || Math.abs(dy) > dragThreshold) {
          hasDraggedRef.current = true;
          // Now enable drag mode since user is actually dragging
          if (!isDragMovingRef.current) {
            isDragMovingRef.current = true;
            setIsDragEnabled(true);
          }
        }
        
        // Only update position if user has started dragging
        if (hasDraggedRef.current) {
          const currentAccelerate = accelerateValue || 1;
          const currentZoomLevel = zoomVars?.zoomLevel || 1;

          // Improve movement tracking with better compensation
          const compensatedDx = (dx * currentAccelerate) / Math.max(currentZoomLevel, 0.1);
          const compensatedDy = (dy * currentAccelerate) / Math.max(currentZoomLevel, 0.1);

          const newX = startPositionRef.current.x + compensatedDx;
          const newY = startPositionRef.current.y + compensatedDy;

          // Real-time position updates for smooth tracking
          translationX.value = newX;
          translationY.value = newY;

          const newPosition = { x: newX, y: newY };

          console.log('🖱️ Real-time drag move:', action._id, {
            gesture: { dx: dx.toFixed(1), dy: dy.toFixed(1) },
            compensated: { dx: compensatedDx.toFixed(1), dy: compensatedDy.toFixed(1) },
            newPosition: { x: newX.toFixed(1), y: newY.toFixed(1) },
            zoom: currentZoomLevel.toFixed(2)
          });

          // Use immediate update during active dragging for better tracking
          immediatePositionUpdate(newPosition);
        }
      },
      onPanResponderRelease: (event, gestureState) => {
        console.log('🎯 PAN RESPONDER RELEASED:', action._id, action.title);
        if (!dragEnabled || isPrimarySchema) return;
        
        const clickDuration = Date.now() - clickStartTimeRef.current;
        const wasClick = !hasDraggedRef.current && clickDuration < 300; // Less than 300ms and no drag

        if (wasClick) {
          // This was a click, not a drag
          console.log('👆 CLICK detected on action card:', action._id);
          
          // CRITICAL: Preserve position before any state changes
          const currentPos = { x: translationX.value, y: translationY.value };
          lastKnownPosition.current = currentPos;
          
          // Commit position immediately to prevent reset
          onPositionChange(currentPos);
          
          // Small delay to ensure position is committed before opening dialog
          setTimeout(() => {
            // Handle the click action
            if (isWorkflowNode && onWorkflowNodeClick) {
              // Workflow node click - open node editor
              onWorkflowNodeClick(action._id || action.id);
            } else if (isPrimarySchema) {
              handlePrimarySchemaClick();
            } else {
              // Pass full action object instead of just actionType
              if (onActionClick) {
                onActionClick(action);
              } else {
                // Fallback to old behavior if onActionClick not provided
                const actionType = normalizeActionType(action.type || 'Create');
                handleActionCardClick(actionType);
              }
            }
          }, 0);
        } else {
          // This was a drag
          console.log('🎯 DRAG completed for:', action._id);
          debouncedConnectionUpdate.cancel();

          const finalPosition = {
            x: translationX.value,
            y: translationY.value,
          };

          console.log('🎯 Action drag complete, final position:', finalPosition);
          lastKnownPosition.current = finalPosition;
          onPositionChange(finalPosition);
        }

        setIsDragEnabled(false);
        isDragMovingRef.current = false;
        hasDraggedRef.current = false;
      },
      onPanResponderTerminate: () => {
        if (!dragEnabled || isPrimarySchema) return;
        
        console.log('🎯 Action drag terminated for:', action._id);

        // CRITICAL: Commit current position to prevent reset on terminate
        const currentPos = { x: translationX.value, y: translationY.value };
        lastKnownPosition.current = currentPos;
        console.log('🎯 Committing position during terminate:', action._id, currentPos);
        onPositionChange(currentPos);

        setIsDragEnabled(false);
        isDragMovingRef.current = false;
        debouncedConnectionUpdate.cancel();
      }
    }), [
      dragEnabled,
      isPrimarySchema,
      action._id,
      action.title,
      action.type,
      translationX,
      translationY,
      startPositionRef,
      clickStartTimeRef,
      hasDraggedRef,
      setIsDragEnabled,
      isDragMovingRef,
      accelerateValue,
      zoomVars,
      immediatePositionUpdate,
      debouncedConnectionUpdate,
      handlePrimarySchemaClick,
      handleActionCardClick,
      normalizeActionType,
      lastKnownPosition,
      onPositionChange
    ]);

    // Cleanup and connection mode handling
    useEffect(() => {
      componentMountedRef.current = true;
      return () => {
        componentMountedRef.current = false;
        debouncedConnectionUpdate.cancel();
      };
    }, [debouncedConnectionUpdate, action._id]);

    // Handle escape key for connection cancellation
    useEffect(() => {
      const handleEscape = (event: any) => {
        if (event.key === 'Escape' && isConnecting && onConnectionCancel) {
          onConnectionCancel();
        }
      };

      if (Platform.OS === 'web') {
        document.addEventListener('keydown', handleEscape);
        return () => document.removeEventListener('keydown', handleEscape);
      }
    }, [isConnecting, onConnectionCancel]);

    // Animated components
    const AnimatedPath = Animated.createAnimatedComponent(Path);
    const animatedProps = useAnimatedProps(() => {
      return {
        d: arrowPath.value,
        stroke: strokeColor.value,
      }
    });

    const modernCardStyles = getModernCardStyle(theme, portrait ? 'large' : 'medium');
    const primaryTextColor = getModernTextColor(theme, 'primary');
    const secondaryTextColor = getModernTextColor(theme, 'secondary');
    
    // Workflow node styling
    const workflowNodeBorderColor = nodeColor || (isWorkflowNode ? '#94a3b8' : undefined);
    const workflowNodeBgColor = isWorkflowNode 
      ? (theme ? `${nodeColor}15` : `${nodeColor}30`) 
      : undefined;

    // Use useAnimatedStyle to properly connect shared values to the View
    const animatedContainerStyle = useAnimatedStyle(() => {
      'worklet';
      return {
        position: 'absolute',
        left: translationX.value,
        top: translationY.value,
        width: cardWidth + 30,
        minHeight: cardHeight,
        zIndex: isDragEnabled ? 1000 : 1,
        overflow: "visible",
      };
    });

    return (
      <Animated.View
        style={animatedContainerStyle}
      >
        {/* Modern Drag Handle - Hidden for Primary Schema */}
        {!isPrimarySchema && (
          <View 
            {...dragPanResponder.panHandlers}
            style={[
              {
                ...styles.modernActionButton,
                position: 'absolute',
                left: -25,
                top: 20,
                zIndex: 15,
                backgroundColor: theme ? 'rgba(59, 130, 246, 1)' : 'rgba(59, 130, 246, 1)',
                borderColor: '#3b82f6',
                opacity: isDragEnabled ? 1 : 0.8
              },
              // Add cursor styling for web
              Platform.OS === 'web' && {
                cursor: (isDragEnabled ? 'grabbing' : 'grab') as any
              }
            ]}
          >
            <MaterialIcons 
              name="drag-indicator" 
              size={20} 
              color={"white"} 
            />
          </View>
        )}

        {/* Connection Points */}
        <Measurable
          style={{ position: 'absolute', minWidth: 280, top: -20, borderRadius: 20, backgroundColor: theme ? 'white' : '#ececec', minHeight: cardHeight, paddingHorizontal: 20, marginHorizontal: 20 }}
          ref={measureRef}
          commitMeasure={undefined}
          onMeasure={measureView}
        >
          {/* Render connectors from action.connectors if available */}
          {action.connectors ? (
            <>
              {/* Input Connectors */}
              {action.connectors.inputs && Array.isArray(action.connectors.inputs) && action.connectors.inputs.map((connector, index) => {
                const connectorId = typeof connector === 'string' ? connector : connector.id;
                const connectorLabel = typeof connector === 'string' ? connector : connector.label;
                const connectorPosition = typeof connector === 'string' ? 'top' : (connector.position || 'top');
                
                // Calculate position based on connector position
                let connectorStyle: any = {};
                if (connectorPosition === 'top') {
                  connectorStyle = { top: -18, left: cardWidth / 2 + (index * 20 - (action.connectors.inputs.length * 10)) - 20, transform: [{ translateX: -8 }] };
                } else if (connectorPosition === 'bottom') {
                  connectorStyle = { bottom: 0, left: cardWidth / 2 + (index * 20 - (action.connectors.inputs.length * 10)), transform: [{ translateX: -8 }] };
                } else if (connectorPosition === 'left') {
                  connectorStyle = { left: -18, top: cardHeight / 2 + (index * 20 - (action.connectors.inputs.length * 10)), transform: [{ translateY: -8 }] };
                } else if (connectorPosition === 'right') {
                  connectorStyle = { right: -18, top: cardHeight / 2 + (index * 20 - (action.connectors.inputs.length * 10)), transform: [{ translateY: -8 }] };
                }
                
                return (
                  <TouchableOpacity
                    key={`input-${connectorId}-${index}`}
                    style={{ position: 'absolute', ...connectorStyle, zIndex: 10 }}
                    onPress={() => {
                      const actionId = action._id || action.id;
                      if (isConnecting && connectionStart && connectionStart !== actionId && onConnectionEnd) {
                        // Pass connector info if available
                        onConnectionEnd(actionId);
                      }
                    }}
                    disabled={!isConnecting || !connectionStart || connectionStart === (action._id || action.id)}
                  >
                    <View style={{
                      width: 16,
                      height: 16,
                      borderRadius: 8,
                      backgroundColor: isConnecting && connectionStart && connectionStart !== (action._id || action.id) 
                        ? '#22c55e' 
                        : '#3b82f6',
                      borderWidth: 3,
                      borderColor: 'white',
                      shadowRadius: 6,
                      shadowOpacity: 0.4,
                      shadowOffset: { width: 0, height: 3 },
                      opacity: isConnecting && connectionStart === (action._id || action.id) ? 0.6 : 1
                    }} />
                    {connectorLabel && (
                      <View style={{
                        position: 'absolute',
                        [connectorPosition === 'top' ? 'bottom' : connectorPosition === 'bottom' ? 'top' : connectorPosition === 'left' ? 'right' : 'left']: 20,
                        backgroundColor: 'rgba(0,0,0,0.7)',
                        paddingHorizontal: 4,
                        paddingVertical: 2,
                        borderRadius: 4
                      }}>
                        <Text style={{ fontSize: 8, color: 'white' }}>{connectorLabel}</Text>
                      </View>
                    )}
                  </TouchableOpacity>
                );
              })}
              
              {/* Output Connectors */}
              {action.connectors.outputs && Array.isArray(action.connectors.outputs) && action.connectors.outputs.map((connector, index) => {
                const connectorId = typeof connector === 'string' ? connector : connector.id;
                const connectorLabel = typeof connector === 'string' ? connector : connector.label;
                const connectorPosition = typeof connector === 'string' ? 'bottom' : (connector.position || 'bottom');
                
                // Calculate position based on connector position
                let connectorStyle: any = {};
                if (connectorPosition === 'top') {
                  connectorStyle = { top: -18, left: cardWidth / 2 + (index * 20 - (action.connectors.outputs.length * 10)), transform: [{ translateX: -8 }] };
                } else if (connectorPosition === 'bottom') {
                  connectorStyle = { bottom: -18, left: cardWidth / 2 + (index * 20 - (action.connectors.outputs.length * 10)), transform: [{ translateX: -8 }] };
                } else if (connectorPosition === 'left') {
                  connectorStyle = { left: -18, top: cardHeight / 2 + (index * 20 - (action.connectors.outputs.length * 10)), transform: [{ translateY: -8 }] };
                } else if (connectorPosition === 'right') {
                  connectorStyle = { right: -18, top: cardHeight / 2 + (index * 20 - (action.connectors.outputs.length * 10)), transform: [{ translateY: -8 }] };
                }
                
                return (
                  <TouchableOpacity
                    key={`output-${connectorId}-${index}`}
                    style={{ position: 'absolute', ...connectorStyle, zIndex: 10 }}
                    onPress={() => {
                      if (onConnectionStart) {
                        onConnectionStart(action._id || action.id);
                      }
                    }}
                    disabled={isConnecting}
                  >
                    <View style={{
                      width: 16,
                      height: 16,
                      borderRadius: 8,
                      backgroundColor: isConnecting && connectionStart === (action._id || action.id) 
                        ? '#ef4444' 
                        : '#10b981',
                      borderWidth: 3,
                      borderColor: 'white',
                      shadowRadius: 6,
                      shadowOpacity: 0.4,
                      shadowOffset: { width: 0, height: 3 },
                      opacity: isConnecting && connectionStart !== (action._id || action.id) ? 0.6 : 1
                    }} />
                    {connectorLabel && (
                      <View style={{
                        position: 'absolute',
                        [connectorPosition === 'top' ? 'bottom' : connectorPosition === 'bottom' ? 'top' : connectorPosition === 'left' ? 'right' : 'left']: 20,
                        backgroundColor: 'rgba(0,0,0,0.7)',
                        paddingHorizontal: 4,
                        paddingVertical: 2,
                        borderRadius: 4
                      }}>
                        <Text style={{ fontSize: 8, color: 'white' }}>{connectorLabel}</Text>
                      </View>
                    )}
                  </TouchableOpacity>
                );
              })}
            </>
          ) : (
            <>
              {/* Fallback: Default connection points if no connectors defined */}
              {/* Left connection dots (incoming connections) - Only show for nodes that accept inputs */}
              {(!isWorkflowNode || (nodeTypeDef && nodeTypeDef.inputs > 0)) && (
                <View style={{ position: 'absolute', left: -18, top: '50%', transform: [{ translateY: -14 }], zIndex: 10 }}>
                  <View style={{
                    width: 14,
                    height: 14,
                    borderRadius: 7,
                    backgroundColor: isWorkflowNode ? (nodeColor || 'rgb(90,192,234)') : 'rgb(90,192,234)',
                    borderWidth: 3,
                    borderColor: 'white',
                    shadowRadius: 5,
                    shadowOpacity: 0.4,
                    shadowOffset: { width: 0, height: 2 }
                  }} />
                </View>
              )}

              {/* Right connection dots (outgoing connections) - Only show for nodes that have outputs */}
              {(!isWorkflowNode || (nodeTypeDef && nodeTypeDef.outputs > 0)) && (
                <TouchableOpacity
                  style={{ position: 'absolute', right: -18, top: '50%', transform: [{ translateY: -14 }], zIndex: 10 }}
                  onPress={() => {
                    if (onConnectionStart) {
                      onConnectionStart(action._id || action.id);
                    }
                  }}
                  disabled={isConnecting}
                >
                  <View style={{
                    width: 14,
                    height: 14,
                    borderRadius: 7,
                    backgroundColor: isConnecting && connectionStart === (action._id || action.id) 
                      ? '#ef4444' 
                      : (isWorkflowNode ? (nodeColor || 'rgb(169,192,255)') : 'rgb(169,192,255)'),
                    borderWidth: 3,
                    borderColor: 'white',
                    shadowRadius: 5,
                    shadowOpacity: 0.4,
                    shadowOffset: { width: 0, height: 2 },
                    opacity: isConnecting && connectionStart !== (action._id || action.id) ? 0.6 : 1
                  }} />
                </TouchableOpacity>
              )}

              {/* Top connection point (for incoming connections) - Only show for nodes that accept inputs */}
              {(!isWorkflowNode || (nodeTypeDef && nodeTypeDef.inputs > 0)) && (
                <TouchableOpacity
                  style={{ position: 'absolute', top: -18, left: cardWidth / 2, transform: [{ translateX: -8 }], zIndex: 10 }}
                  onPress={() => {
                    const actionId = action._id || action.id;
                    if (isConnecting && connectionStart && connectionStart !== actionId && onConnectionEnd) {
                      onConnectionEnd(actionId);
                    }
                  }}
                  disabled={!isConnecting || !connectionStart || connectionStart === (action._id || action.id)}
                >
                  <View style={{
                    width: 16,
                    height: 16,
                    borderRadius: 8,
                    backgroundColor: isConnecting && connectionStart && connectionStart !== (action._id || action.id) 
                      ? '#22c55e' 
                      : (isWorkflowNode ? (nodeColor || 'rgb(169,192,255)') : 'rgb(169,192,255)'),
                    borderWidth: 3,
                    borderColor: 'white',
                    shadowRadius: 6,
                    shadowOpacity: 0.4,
                    shadowOffset: { width: 0, height: 3 },
                    opacity: isConnecting && connectionStart === (action._id || action.id) ? 0.6 : 1
                  }} />
                </TouchableOpacity>
              )}

              {/* Bottom connection point (for outgoing connections) */}
              {showArrows && (
                  <View style={{  position: 'absolute', bottom: 0, left: cardWidth / 2, transform: [{ translateX: -8 }], zIndex: 10 }}>
                  <View style={{
                    width: 16,
                    height: 16,
                    borderRadius: 8,
                    backgroundColor: 'rgb(90,192,234)',
                      borderWidth: 30,
                    borderColor: 'white',
                    shadowRadius: 6,
                      top: 200,
                    shadowOpacity: 0.4,
                    shadowOffset: { width: 0, height: 3 }
                  }} />
                </View>
              )}
            </>
          )}
        </Measurable>

        {/* Main Action Card Content */}
        <Animatable.View 
          {...(dragEnabled && !isPrimarySchema ? dragPanResponder.panHandlers : {})}
          transition={'maxHeight'} 
          style={[
            {
              ...modernCardStyles,
              marginTop: -30,
              overflow: 'hidden',
              width: cardWidth,
              minHeight: cardHeight + 40,
              maxHeight: cardHeight + 40,
              flexDirection: portrait ? 'column' : 'row',
              gap: 16,
              borderWidth: isPrimarySchema ? 4 : (isWorkflowNode ? 3 : (isDragEnabled ? 3 : 0)),
              borderColor: isPrimarySchema 
                ? '#10b981' 
                : (isWorkflowNode ? (workflowNodeBorderColor || '#94a3b8') : '#3b82f6'),
              borderStyle: isPrimarySchema ? 'solid' : (isDragEnabled ? 'dashed' : 'solid'),
              backgroundColor: isWorkflowNode ? workflowNodeBgColor : undefined,
              transform: isPrimarySchema 
                ? [{ scale: 1.1 }] 
                : (isDragEnabled ? [{ scale: 1.02 }] : [{ scale: 1 }]),
              shadowOpacity: isPrimarySchema ? 0.3 : (isWorkflowNode ? 0.2 : 0.1),
              shadowRadius: isPrimarySchema ? 12 : (isWorkflowNode ? 8 : 6),
              shadowColor: isPrimarySchema ? '#10b981' : (isWorkflowNode ? (workflowNodeBorderColor || '#94a3b8') : '#3b82f6'),
              elevation: isPrimarySchema ? 8 : (isWorkflowNode ? 6 : 4),
            },
            // Add cursor styling for web
            Platform.OS === 'web' && {
              cursor: isPrimarySchema
                ? 'pointer' as any
                : (isDragEnabled ? 'grabbing' : 'pointer') as any
            }
          ]}
        >
          {/* Click handler for Primary Schema (which doesn't have pan responder) */}
          {isPrimarySchema && (
            <TouchableOpacity
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                zIndex: 1,
                backgroundColor: 'transparent'
              }}
              onPress={() => {
                const currentPos = { x: translationX.value, y: translationY.value };
                lastKnownPosition.current = currentPos;
                onPositionChange(currentPos);
                handlePrimarySchemaClick();
              }}
              activeOpacity={0.9}
            />
          )}

          {/* Action Icon/Type Section */}
          <View style={{ 
            ...styles.modernImageContainer,
            flex: portrait ? 2 : 1,
            maxHeight: portrait ? 120 : 60,
            alignSelf: 'stretch',
            backgroundColor: isPrimarySchema
              ? (theme ? 'rgba(16, 185, 129, 0.1)' : 'rgba(16, 185, 129, 0.2)')
              : (theme ? 'rgba(99, 102, 241, 0.1)' : 'rgba(99, 102, 241, 0.2)'),
            justifyContent: 'center',
            alignItems: 'center',
            gap: 20,
            overflow: 'hidden'
          }}>
            {/* Show template image if available */}
            {action.templateImage ? (
              <ErrorBoundary>
                <Image
                  source={{ uri: action.templateImage }}
                  style={{
                    width: '100%',
                    height: '100%',
                    resizeMode: 'cover'
                  }}
                  onError={(error) => {
                    console.warn('Failed to load template image:', error);
                  }}
                />
                {/* Overlay badge */}
                <View style={{
                  position: 'absolute',
                  top: 8,
                  right: 8,
                  backgroundColor: 'rgba(0,0,0,0.7)',
                  paddingHorizontal: 8,
                  paddingVertical: 4,
                  borderRadius: 6
                }}>
                  <Text style={{ fontSize: 9, fontWeight: '600', color: 'white' }}>
                    TEMPLATE
                  </Text>
                </View>
              </ErrorBoundary>
            ) : (
              <>
                  <MaterialIcons
                    name={isPrimarySchema 
                      ? "schema" 
                      : (isWorkflowNode && nodeIcon ? nodeIcon : "settings")}
                    size={isPrimarySchema ? 48 : (portrait ? 48 : 32)}
                    color={isPrimarySchema
                      ? (theme ? "#10b981" : "#34d399")
                      : (isWorkflowNode 
                          ? (nodeColor || (theme ? "#6366f1" : "#818cf8"))
                          : (theme ? "#6366f1" : "#818cf8"))
                    }
                  />
                  {isPrimarySchema && (
                    <Text style={{
                      fontSize: 12,
                      fontWeight: '600',
                      color: theme ? "#10b981" : "#34d399",
                      marginTop: 8
                    }}>
                      PRIMARY SCHEMA
                    </Text>
                  )}
                  {isWorkflowNode && nodeCategory && (
                    <View style={{
                      position: 'absolute',
                      top: 8,
                      left: 8,
                      backgroundColor: nodeColor || '#94a3b8',
                      paddingHorizontal: 6,
                      paddingVertical: 2,
                      borderRadius: 4
                    }}>
                      <Text style={{
                        fontSize: 9,
                        fontWeight: '700',
                        color: 'white',
                        textTransform: 'uppercase'
                      }}>
                        {nodeCategory}
                      </Text>
                    </View>
                  )}
                  {isAgenticFlow && (
                    <View style={{
                      position: 'absolute',
                      top: 8,
                      right: 8,
                      backgroundColor: theme ? '#fef3c7' : '#78350f',
                      paddingHorizontal: 8,
                      paddingVertical: 4,
                      borderRadius: 6,
                      flexDirection: 'row',
                      alignItems: 'center',
                      gap: 4,
                      borderWidth: 1,
                      borderColor: theme ? '#fcd34d' : '#fbbf24'
                    }}>
                      <MaterialIcons name="psychology" size={12} color={theme ? '#d97706' : '#fbbf24'} />
                      <Text style={{
                        fontSize: 9,
                        fontWeight: '700',
                        color: theme ? '#d97706' : '#fbbf24',
                        textTransform: 'uppercase'
                      }}>
                        AI
                      </Text>
                    </View>
                  )}
                </>
            )}
          </View>

          {/* Action Info Section */}
          <View style={{
            ...styles.modernColumn,
            padding: portrait ? 16 : 12,
            backgroundColor: theme 
              ? 'rgba(99, 102, 241, 0.08)' 
              : 'rgba(99, 102, 241, 0.15)',
            borderRadius: 16,
            gap: portrait ? 8 : 4
          }}>
            <Text 
              adjustsFontSizeToFit 
              numberOfLines={1} 
              style={{ 
                ...styles.modernCardTitle,
                color: primaryTextColor,
                fontSize: portrait ? 18 : 14,
                fontWeight: '700',
                userSelect: 'none'
              }}
            >
              {action.title || action.name || 'Action'}
            </Text>
            
            <Text 
              adjustsFontSizeToFit 
              numberOfLines={portrait ? 2 : 1} 
              style={{ 
                ...styles.modernFieldValue,
                color: secondaryTextColor,
                fontSize: portrait ? 13 : 11,
                fontWeight: '500',
                userSelect: 'none'
              }}
            >
              {action.description || 'No description'}
            </Text>


          </View>
          {/* Conditions Badge */}
          {action.conditions && action.conditions.length > 0 && (
            <View style={{
              flexDirection: 'row',
              alignItems: 'center',
              backgroundColor: theme ? '#fef3c7' : '#78350f',
              paddingHorizontal: 8,
              paddingVertical: 4,
              borderRadius: 6,
              alignSelf: 'flex-start',
              marginTop: 4
            }}>
              <MaterialIcons name="rule" size={12} color={theme ? '#d97706' : '#fbbf24'} />
              <Text style={{
                fontSize: 10,
                fontWeight: '600',
                color: theme ? '#d97706' : '#fbbf24',
                marginLeft: 4
              }}>
                {action.conditions.length} condition{action.conditions.length !== 1 ? 's' : ''}
              </Text>
            </View>
          )}

          {portrait && (
            <>
              {/* Action Type and Status */}
              <View style={styles.modernColumn}>
                {action.type && (
                  <Text style={{
                    ...styles.modernFieldLabel,
                    color: secondaryTextColor,
                    fontSize: 11,
                    userSelect: 'none'
                  }}>
                    Type: {action.type}
                  </Text>
                )}
                {action.status && (
                  <Text style={{
                    ...styles.modernFieldLabel,
                    color: secondaryTextColor,
                    fontSize: 11,
                    userSelect: 'none'
                  }}>
                    Status: {action.status}
                  </Text>
                )}
              </View>

              {/* Data Schema Info */}
              {action.dataSchema && Array.isArray(action.dataSchema) && (
                <View style={{
                  ...styles.modernRow,
                  backgroundColor: theme
                    ? 'rgba(34, 197, 94, 0.1)'
                    : 'rgba(34, 197, 94, 0.2)',
                  padding: 12,
                  borderRadius: 12,
                  alignItems: 'center',
                  marginTop: 8
                }}>
                  <MaterialIcons
                    name="data-usage"
                    size={20}
                    color="#22c55e"
                  />
                  <Text
                    style={{
                      ...styles.modernStatsNumber,
                      fontSize: 16,
                      color: '#22c55e',
                      fontWeight: '600',
                      marginLeft: 8,
                      userSelect: 'none'
                    }}
                  >
                    {action.dataSchema.length} fields
                  </Text>
                </View>
              )}

              {/* Settings Button */}
              {!isPrimarySchema && (
                <View style={{
                  position: 'absolute',
                  top: 8,
                  right: 8,
                  zIndex: 10
                }}>
                  <TouchableOpacity
                    onPress={() => setModalVisible(true)}
                    style={{
                      backgroundColor: theme ? 'rgba(255, 255, 255, 0.9)' : 'rgba(0, 0, 0, 0.1)',
                      borderRadius: 20,
                      padding: 8,
                      shadowColor: '#000',
                      shadowOffset: { width: 0, height: 2 },
                      shadowOpacity: 0.1,
                      shadowRadius: 4,
                      elevation: 2
                    }}
                  >
                    <Feather
                      name='settings'
                      size={16}
                      color={theme ? '#6366f1' : '#818cf8'}
                    />
                  </TouchableOpacity>
                </View>
              )}
            </>
          )}
        </Animatable.View>

        {/* Arrow Visualization */}
        {showArrows && !hideArrows && !isDragEnabled && (
          <Animatable.View 
            transition={'top'} 
            style={{ 
              overflow: 'visible', 
              position: 'absolute', 
              top: cardHeight / 2,
              left: cardWidth / 2,
              zIndex: -100, 
              maxHeight: 1000,
              maxWidth: 1000,
              backgroundColor: 'transparent',
              pointerEvents: 'none',
            }}
          >
            <Svg 
              width={1000} 
              height={1000} 
              style={{ 
                overflow: "visible", 
                position: "absolute",
                left: -500,
                top: -500
              }}
            >
              <Defs>
                <Marker 
                  id={`arrowhead${action._id}`} 
                  viewBox='0 0 10 10' 
                  refX={8} 
                  refY={5} 
                  markerWidth={8} 
                  markerHeight={8}
                  orient="auto"
                >
                  <Path
                    d={"M0,0 L0,10 L10,5 z"} 
                    fill="#3b82f6" 
                  />
                </Marker>
              </Defs>
              <AnimatedPath
                animatedProps={animatedProps} 
                fill="transparent" 
                stroke="#3b82f6" 
                strokeWidth={3} 
                markerEnd={`url(#arrowhead${action._id})`} 
                opacity={0.8}
                transform="translate(500,500)"
              />
            </Svg>
          </Animatable.View>
        )}

        {/* Connection Mode Indicator */}
        {isConnecting && (
          <View style={{
            position: 'absolute',
            top: -40,
            left: cardWidth / 2 - 50,
            backgroundColor: connectionStart === action._id ? '#ef4444' : '#22c55e',
            paddingHorizontal: 12,
            paddingVertical: 6,
            borderRadius: 20,
            zIndex: 1000,
            shadowColor: '#000',
            shadowOffset: { width: 0, height: 2 },
            shadowOpacity: 0.3,
            shadowRadius: 4,
            elevation: 5
          }}>
            <Text style={{
              color: 'white',
              fontSize: 12,
              fontWeight: 'bold',
              textAlign: 'center'
            }}>
              {connectionStart === action._id ? 'Connecting...' : 'Click to connect'}
            </Text>
          </View>
        )}

        {/* Debug Position Indicator - Shows during dragging */}
        {isDragEnabled && __DEV__ && (
          <View style={{
            position: 'absolute',
            top: -35,
            left: 10,
            backgroundColor: 'rgba(0,0,0,0.8)',
            paddingHorizontal: 8,
            paddingVertical: 4,
            borderRadius: 4,
            zIndex: 20
          }}>
            <Text style={{ color: 'white', fontSize: 10, fontWeight: 'bold' }}>
              {translationX.value.toFixed(0)}, {translationY.value.toFixed(0)}
            </Text>
          </View>
        )}

        {/* Sophisticated Settings Modal */}
        <Modal
          animationType="fade"
          transparent={true}
          visible={modalVisible}
          onRequestClose={() => setModalVisible(false)}
        >
          <View style={{
            flex: 1,
            backgroundColor: 'rgba(0, 0, 0, 0.6)',
            justifyContent: 'center',
            alignItems: 'center',
          }}>
            <Animatable.View
              animation={modalVisible ? "zoomIn" : "zoomOut"}
              duration={300}
              style={{
                width: '92%',
                maxWidth: 800,
                height: '88%',
                maxHeight: 700,
                backgroundColor: theme ? '#ffffff' : '#374151',
                borderRadius: 16,
                shadowColor: '#000',
                shadowOffset: { width: 0, height: 8 },
                shadowOpacity: 0.25,
                shadowRadius: 16,
                elevation: 8,
              }}
            >
              <YStack flex={1} space="$0">
                {/* Header Section */}
                <YStack
                  padding="$5"
                  paddingBottom="$4"
                  borderBottomWidth={1}
                  borderBottomColor={theme ? '#e5e7eb' : '#4b5563'}
                  backgroundColor={theme ? '#f8fafc' : '#1f2937'}
                  borderTopLeftRadius={16}
                  borderTopRightRadius={16}
                >
                  <H2
                    fontSize="$6"
                    fontWeight="700"
                    color={theme ? '#1f2937' : '#f9fafb'}
                    marginBottom="$1"
                  >
                    {action.title} Settings
                  </H2>
                  <SizableText
                    color={theme ? '#6b7280' : '#9ca3af'}
                    size="$4"
                    lineHeight="$5"
                  >
                    Configure and customize the settings for this action component.
                  </SizableText>
                </YStack>

                {/* Content Section */}
                <ErrorBoundary>
                  <YStack
                    flex={1}
                    padding="$5"
                    backgroundColor={theme ? '#ffffff' : '#374151'}
                    style={{
                      minHeight: 300,
                    }}
                  >
                    {ComponentToRender ? (
                      <ErrorBoundary>
                        {React.createElement(ComponentToRender, {
                          ...action,
                          ref: formRef
                        } as any)}
                      </ErrorBoundary>
                    ) : (
                      <YStack
                        flex={1}
                        justifyContent="center"
                        alignItems="center"
                        padding="$8"
                      >
                        <SizableText
                          size="$5"
                          color={theme ? '#6b7280' : '#9ca3af'}
                          textAlign="center"
                        >
                          No component defined for this action type.
                        </SizableText>
                      </YStack>
                    )}
                  </YStack>
                </ErrorBoundary>

                {/* Footer Section */}
                <YStack
                  padding="$5"
                  paddingTop="$4"
                  borderTopWidth={1}
                  borderTopColor={theme ? '#e5e7eb' : '#4b5563'}
                  backgroundColor={theme ? '#f8fafc' : '#1f2937'}
                  borderBottomLeftRadius={16}
                  borderBottomRightRadius={16}
                >
                  <XStack gap="$3" justifyContent="flex-end">
                    <Button
                      backgroundColor={theme ? '#f3f4f6' : '#4b5563'}
                      borderColor={theme ? '#d1d5db' : '#6b7280'}
                      borderWidth={1}
                      borderRadius={12}
                      paddingHorizontal="$5"
                      paddingVertical="$3"
                      pressStyle={{
                        backgroundColor: theme ? '#e5e7eb' : '#374151',
                        scale: 0.98,
                      }}
                      aria-label="Close"
                      onPress={() => {
                        console.log("discarding changes");
                        if (Platform.OS === 'web') {
                          if (formRef.current?.hasChanges?.()) {
                            if (window.confirm("Discard changes? This will discard any unsaved changes.")) {
                              console.log('Discard Pressed');
                              setModalVisible(false);
                            }
                          } else {
                            setModalVisible(false);
                          }
                        } else {
                          if (formRef.current?.hasChanges?.()) {
                            Alert.alert(
                              "Discard changes?",
                              'This will discard any unsaved changes.',
                              [
                                {
                                  text: 'Cancel',
                                  onPress: () => console.log('Cancel Pressed'),
                                  style: 'cancel'
                                },
                                {
                                  text: 'OK',
                                  onPress: () => {
                                    console.log('Discard Pressed');
                                    setModalVisible(false);
                                  }
                                }
                              ]
                            );
                          } else {
                            setModalVisible(false);
                          }
                        }
                      }}
                    >
                      <SizableText
                        color={theme ? '#6b7280' : '#d1d5db'}
                        fontWeight="600"
                        size="$4"
                      >
                        Discard
                      </SizableText>
                    </Button>
                    <Button
                      backgroundColor="rgb(90,192,234)"
                      borderRadius={12}
                      paddingHorizontal="$5"
                      paddingVertical="$3"
                      pressStyle={{
                        backgroundColor: '#2563eb',
                        scale: 0.98,
                      }}
                      shadowColor="#000"
                      shadowOffset={{ width: 0, height: 2 }}
                      shadowOpacity={0.1}
                      shadowRadius={4}
                      elevation={2}
                      aria-label="Save"
                      onPress={() => {
                        formRef.current?.handleSubmit?.();
                        setModalVisible(false);
                      }}
                    >
                      <SizableText
                        color="#ffffff"
                        fontWeight="700"
                        size="$4"
                      >
                        Save Changes
                      </SizableText>
                    </Button>
                  </XStack>
                </YStack>
              </YStack>
            </Animatable.View>
          </View>
        </Modal>

        {/* Action Dialog */}
        <ActionDialog
          visible={showActionDialog}
          actionType={currentActionType}
          dataId={dataId}
          onClose={() => {
            console.log('🎯 ActionDialog onClose called');
            setShowActionDialog(false);
          }}
          onSave={(actionData) => {
            console.log('🎯 Action saved from dialog:', actionData);
            setShowActionDialog(false);
          }}
        />

        {/* Schema Popover for Primary Schema Cards */}
        {isPrimarySchema && (
          <Popover
            placement="top"
            size="$5"
            allowFlip
            open={showSchemaPopover}
            onOpenChange={setShowSchemaPopover}
          >
            <Popover.Trigger asChild>
              <View />
            </Popover.Trigger>
            <Popover.Content
              backgroundColor={theme ? '#ffffff' : '#1f2937'}
              borderColor={theme ? '#e5e7eb' : '#374151'}
              borderWidth={1}
              borderRadius={12}
              padding="$4"
              maxWidth={400}
              shadowColor="#000"
              shadowOffset={{ width: 0, height: 4 }}
              shadowOpacity={0.3}
              shadowRadius={8}
              elevation={8}
            >
              <YStack space="$3">
                <H2 color={theme ? '#111827' : '#f9fafb'} size="$6">
                  🏛️ Schema Structure
                </H2>
                <SizableText color={theme ? '#6b7280' : '#d1d5db'} size="$4">
                  {action.title || 'Main Data Schema'}
                </SizableText>

                {/* Schema Fields */}
                {action.dataSchema && action.dataSchema.length > 0 ? (
                  <YStack space="$2">
                    <SizableText color={theme ? '#374151' : '#e5e7eb'} size="$4" fontWeight="600">
                      Fields:
                    </SizableText>
                    {action.dataSchema.map((field: any, index: number) => (
                      <XStack key={index} space="$2" alignItems="center">
                        <SizableText color={theme ? '#6b7280' : '#9ca3af'} size="$3">
                          {field.type || 'string'}
                        </SizableText>
                        <SizableText color={theme ? '#111827' : '#f9fafb'} size="$4" fontWeight="500">
                          {field.name}
                        </SizableText>
                        {field.required && (
                          <SizableText color="#ef4444" size="$3" fontWeight="600">
                            *
                          </SizableText>
                        )}
                      </XStack>
                    ))}
                  </YStack>
                ) : (
                  <YStack space="$2">
                    <SizableText color={theme ? '#6b7280' : '#9ca3af'} size="$4">
                      This schema contains the following default action types:
                    </SizableText>
                    {[
                      { name: 'Create', icon: '➕', description: 'Add new records' },
                      { name: 'Read', icon: '👁️', description: 'View existing records' },
                      { name: 'Update', icon: '✏️', description: 'Modify existing records' },
                      { name: 'Delete', icon: '🗑️', description: 'Remove records' }
                    ].map((actionType, index) => (
                      <XStack key={index} space="$3" alignItems="center">
                        <SizableText size="$4">{actionType.icon}</SizableText>
                        <SizableText color={theme ? '#111827' : '#f9fafb'} size="$4" fontWeight="500">
                          {actionType.name}
                        </SizableText>
                        <SizableText color={theme ? '#6b7280' : '#9ca3af'} size="$3">
                          {actionType.description}
                        </SizableText>
                      </XStack>
                    ))}
                  </YStack>
                )}

                {/* Description */}
                {action.description && (
                  <YStack space="$2">
                    <SizableText color={theme ? '#374151' : '#e5e7eb'} size="$4" fontWeight="600">
                      Description:
                    </SizableText>
                    <SizableText color={theme ? '#6b7280' : '#9ca3af'} size="$4">
                      {action.description}
                    </SizableText>
                  </YStack>
                )}

                {/* Close Button */}
                <XStack justifyContent="flex-end" marginTop="$3">
                  <Button
                    size="$3"
                    backgroundColor={theme ? '#f3f4f6' : '#374151'}
                    borderColor={theme ? '#d1d5db' : '#4b5563'}
                    borderWidth={1}
                    onPress={() => setShowSchemaPopover(false)}
                  >
                    <SizableText color={theme ? '#374151' : '#f9fafb'} size="$4" fontWeight="600">
                      Close
                    </SizableText>
                  </Button>
                </XStack>
              </YStack>
            </Popover.Content>
          </Popover>
        )}
      </Animated.View>
    );
  }
);

const DraggableActionCard = React.memo(DraggableActionCardInner, (prevProps, nextProps) => {
  // Return true to skip re-render, false to re-render
  
  // Only skip re-render if nothing important has changed
  // This is a more permissive approach that allows internal state updates
  
  // Different action = must re-render
  if (prevProps.action._id !== nextProps.action._id) {
    return false;
  }
  
  // Check if critical props changed
  const criticalPropsChanged = (
    prevProps.dragEnabled !== nextProps.dragEnabled ||
    prevProps.isPrimarySchema !== nextProps.isPrimarySchema ||
    prevProps.showArrows !== nextProps.showArrows ||
    prevProps.portrait !== nextProps.portrait ||
    prevProps.dataId !== nextProps.dataId
  );
  
  if (criticalPropsChanged) {
    return false; // Allow re-render
  }
  
  // Check if position changed significantly (more than 10px to reduce re-renders during pan/zoom)
  const positionChanged = (
    Math.abs(prevProps.initialPosition.x - nextProps.initialPosition.x) > 10 ||
    Math.abs(prevProps.initialPosition.y - nextProps.initialPosition.y) > 10
  );
  
  // If position changed significantly, allow re-render
  if (positionChanged) {
    return false;
  }
  
  // Otherwise, skip re-render - nothing important changed
  // This allows internal state (like dialog open/close) to work properly
  return true;
});

DraggableActionCard.displayName = 'DraggableActionCard';

export default DraggableActionCard;
